import React from 'react';
import { FaArrowLeft, FaArrowRight, FaListOl, FaRandom } from 'react-icons/fa';
import {
  studyProgressDotTitle,
  studyProgressDotVisualState
} from '../../../utils/studySessionDotState';

/**
 * StudyProgressStrip
 * Prev/next grouped left; dot tabs aligned right.
 */
const StudyProgressStrip = ({
  total = 0,
  currentIndex = 0,
  cards = [],
  sessionCardStatuses = [],
  onDotClick,
  onPrev,
  onNext,
  onOpenCookbookIndex,
  cookbookIndexOpen = false,
  prevDisabled = false,
  nextDisabled = false,
  nextHighlighted = false,
  showProgressDots = true,
  practiceNavMode = null,
  onPracticeNavModeChange,
  prevLabel = 'Prev',
  nextLabel = 'Next'
}) => {
  if (total <= 0) return null;

  const showDots = showProgressDots && total > 1;
  const showPracticeNavToggle =
    !showProgressDots && total > 1 && practiceNavMode && typeof onPracticeNavModeChange === 'function';
  const showNavTrailing = showDots || showPracticeNavToggle;
  const currentCardNumber = Math.min(currentIndex + 1, total);
  const countLabel = `${currentCardNumber} / ${total}`;
  const countTitle = typeof onOpenCookbookIndex === 'function'
    ? cookbookIndexOpen
      ? `Close cookbook index (${countLabel})`
      : `Open cookbook index (${countLabel})`
    : countLabel;

  return (
    <div className="study-shell__progress-strip">
      <nav
        className={[
          'study-shell__progress-nav',
          !showNavTrailing && 'study-shell__progress-nav--no-dots'
        ]
          .filter(Boolean)
          .join(' ')}
        aria-label={`Card ${Math.min(currentIndex + 1, total)} of ${total}`}
      >
        <div className="study-shell__progress-nav-buttons">
          <button
            type="button"
            className="study-shell__progress-nav-btn"
            onClick={onPrev}
            disabled={prevDisabled}
            title={prevLabel}
            aria-label={prevLabel}
          >
            <FaArrowLeft className="study-shell__progress-nav-icon" aria-hidden />
          </button>
          <button
            type="button"
            className={[
              'study-shell__progress-nav-count',
              typeof onOpenCookbookIndex === 'function' &&
              'study-shell__progress-nav-count--clickable',
              cookbookIndexOpen && 'study-shell__progress-nav-count--active'
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={onOpenCookbookIndex}
            disabled={typeof onOpenCookbookIndex !== 'function'}
            title={countTitle}
            aria-label={countTitle}
          >
            {countLabel}
          </button>
          <button
            type="button"
            className={[
              'study-shell__progress-nav-btn',
              nextHighlighted && 'study-shell__progress-nav-btn--highlight'
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={onNext}
            disabled={nextDisabled}
            title={nextLabel}
            aria-label={nextLabel}
          >
            <FaArrowRight className="study-shell__progress-nav-icon" aria-hidden />
          </button>
        </div>

        {showPracticeNavToggle && (
          <div
            className="study-shell__practice-nav-toggle"
            role="group"
            aria-label="Practice navigation mode"
          >
            <button
              type="button"
              className={[
                'study-shell__practice-nav-toggle-btn',
                practiceNavMode === 'random' && 'study-shell__practice-nav-toggle-btn--active'
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => onPracticeNavModeChange('random')}
              aria-pressed={practiceNavMode === 'random'}
              aria-label="Random snippet after Check"
              title="After Check, go to a random snippet"
            >
              <FaRandom className="study-shell__practice-nav-toggle-icon" aria-hidden />
              <span className="study-shell__practice-nav-toggle-label">Random</span>
            </button>
            <button
              type="button"
              className={[
                'study-shell__practice-nav-toggle-btn',
                practiceNavMode === 'index' && 'study-shell__practice-nav-toggle-btn--active'
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => onPracticeNavModeChange('index')}
              aria-pressed={practiceNavMode === 'index'}
              aria-label="Next snippet in index order after Check"
              title="After Check, go to the next snippet in the index"
            >
              <FaListOl className="study-shell__practice-nav-toggle-icon" aria-hidden />
              <span className="study-shell__practice-nav-toggle-label">In order</span>
            </button>
          </div>
        )}

        {showDots && (
          <div className="study-shell__progress-dots" role="tablist" aria-label="Cards in this session">
            {Array.from({ length: total }).map((_, index) => {
              const card = cards[index];
              const isActive = index === currentIndex;
              const dotColor = studyProgressDotVisualState({
                index,
                currentIndex,
                cardProgress: card?.progress
              });
              const dotClass = [
                'study-shell__progress-dot',
                dotColor === 'green' && 'study-shell__progress-dot--green',
                dotColor === 'yellow' && 'study-shell__progress-dot--yellow',
                isActive && 'study-shell__progress-dot--active'
              ]
                .filter(Boolean)
                .join(' ');

              const dotTitle = studyProgressDotTitle(dotColor, index, total, isActive, card?.progress);

              return (
                <button
                  key={index}
                  type="button"
                  className={dotClass}
                  onClick={() => onDotClick && onDotClick(index)}
                  title={dotTitle}
                  aria-label={dotTitle}
                  aria-selected={isActive}
                  role="tab"
                />
              );
            })}
          </div>
        )}
      </nav>
    </div>
  );
};

export default StudyProgressStrip;
